// Generated from products/analytics/integrations/chatPublic.ts; run analytics generate:site-runtime.
import {
  analyticsEventId,
  createAnalyticsClient,
  createBrowserEnvironment,
  type AnalyticsClient,
  type AnalyticsClientOptions,
} from "./client.gen";
import { BrowserAnalyticsConfigSchema } from "./domainConfig.gen";

export type ChatPublicSurface = "landing" | "chat";
export interface ChatPublicLocation {
  protocol: string;
  hostname: string;
  pathname: string;
  search: string;
  hash: string;
}

/** Exact public entry paths only. Never infer privacy from a route prefix. */
export function chatPublicPage(
  surface: ChatPublicSurface,
  location: ChatPublicLocation,
): string | null {
  if (location.protocol !== "https:" || location.search !== "" || location.hash !== "") return null;
  if (surface === "landing") return location.pathname === "/" ? "/" : null;
  return ["/chat/", "/chat/channels"].includes(location.pathname) ? "/chat/" : null;
}

export function chatAnalyticsOptedOut(
  navigator: { doNotTrack?: string | null; globalPrivacyControl?: boolean },
  legacyDnt?: string,
): boolean {
  return (
    navigator.globalPrivacyControl === true ||
    ["1", "yes"].includes(navigator.doNotTrack ?? "") ||
    legacyDnt === "1"
  );
}

export interface ChatPublicAnalytics {
  update(): void;
  openChat(): void;
  close(): void;
}

/** No account, message, room, arbitrary action or identity API is exposed. */
export function createChatPublicAnalytics(options: {
  surface: ChatPublicSurface;
  configs: unknown;
  location: () => ChatPublicLocation;
  optedOut: () => boolean;
  environment?: AnalyticsClientOptions["environment"];
  createClient?: (options: AnalyticsClientOptions) => AnalyticsClient;
}): ChatPublicAnalytics {
  let client: AnalyticsClient | null = null;
  let closed = false;
  const viewed = new Set<string>();
  const allowed = () => {
    try {
      return (
        !closed &&
        !options.optedOut() &&
        chatPublicPage(options.surface, options.location()) !== null
      );
    } catch {
      return false;
    }
  };
  const stop = () => {
    client?.close();
    client = null;
  };
  const update = () => {
    try {
      if (!allowed()) {
        stop();
        return;
      }
      const location = options.location();
      const page = chatPublicPage(options.surface, location);
      const parsed = BrowserAnalyticsConfigSchema.array().max(2).safeParse(options.configs);
      if (
        !parsed.success ||
        new Set(parsed.data.map((c) => c.hostname)).size !== parsed.data.length
      ) {
        stop();
        return;
      }
      const config = parsed.data.find((c) => c.hostname === location.hostname);
      if (!config || page === null) {
        stop();
        return;
      }
      if (!client) {
        const environment = options.environment ?? createBrowserEnvironment();
        client = (options.createClient ?? createAnalyticsClient)({
          config,
          extendedTelemetry: false,
          captureErrors: false,
          captureEngagement: false,
          collectionAllowed: allowed,
          environment: {
            ...environment,
            locationPathname: () => page,
            locationSearch: () => "",
            title: () => (options.surface === "chat" ? "Public Chat entry" : "Public Chat landing"),
            referrer: () => "",
          },
        });
      }
      if (!viewed.has(page)) {
        client.track({
          type: analyticsEventId.pageViewed,
          payload: {
            title: options.surface === "chat" ? "Public Chat entry" : "Public Chat landing",
          },
        });
        viewed.add(page);
      }
    } catch {
      try {
        stop();
      } catch {
        /* Analytics must never interrupt Chat. */
      }
    }
  };
  return {
    update,
    openChat() {
      try {
        update();
        if (options.surface !== "landing" || !allowed() || !client) return;
        client.track({
          type: analyticsEventId.siteAction,
          payload: { name: "public_navigation_clicked", resourceId: "open-chat" },
        });
        void client.flush().catch(() => undefined);
      } catch {
        /* Navigation is independent of analytics. */
      }
    },
    close() {
      closed = true;
      try {
        stop();
      } catch {
        /* Safe cleanup. */
      }
    },
  };
}
